import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Check, ChevronDown, ChevronUp, Link2, Loader2, Pencil, Plus, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';

import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { AdminPage, SaveBar } from '../../../components/design-system/admin';
import { TranslatableInput } from '../../../common/components/TranslatableInput';
import { TranslatableChipInput } from '../../../common/components/TranslatableChipInput';
import { useProductFamiliesApi } from '../../../common/hooks/use-product-families-api.hook';
import { useResolveTranslation } from '../../../common/hooks/use-resolve-translation.hook';
import {
  addProductFamily,
  updateProductFamily,
  setActiveFamily,
} from '../../../redux/productFamilies/productFamiliesActions';
import {
  ProductFamily,
  FamilyAttribute,
  VariantAxisConfig,
  VisualizationConfig,
  AttributeFieldType,
  VariantAxisValueType,
  CreateProductFamilyPayload,
} from '../../../common/models/product-family.model';
import { CORE_ATTRIBUTES } from '../../../common/models/product-family.defaults';
import { resolveTranslation } from '../../../common/utils/translatable-string';
import {
  EditorSection,
  EditorHeader,
  LangRowSpacer,
  ToggleField,
  generateSlug,
} from '../../../modules/superadmin/catalog-config';

// =============================================================================
// Constants (labels are translation keys under ADMIN_CATALOG.FAMILY_EDITOR)
// =============================================================================

const FIELD_TYPES: { value: AttributeFieldType; labelKey: string }[] = [
  { value: 'text', labelKey: 'FIELD_TYPES.TEXT' },
  { value: 'number', labelKey: 'FIELD_TYPES.NUMBER' },
  { value: 'textarea', labelKey: 'FIELD_TYPES.TEXTAREA' },
  { value: 'select', labelKey: 'FIELD_TYPES.SELECT' },
  { value: 'multioption', labelKey: 'FIELD_TYPES.MULTIOPTION' },
  { value: 'checkbox', labelKey: 'FIELD_TYPES.CHECKBOX' },
  { value: 'color', labelKey: 'FIELD_TYPES.COLOR' },
  { value: 'url', labelKey: 'FIELD_TYPES.URL' },
  { value: 'dimensions', labelKey: 'FIELD_TYPES.DIMENSIONS' },
  { value: 'images', labelKey: 'FIELD_TYPES.IMAGES' },
];

const AXIS_VALUE_TYPES: { value: VariantAxisValueType; labelKey: string; descriptionKey: string }[] = [
  { value: 'string', labelKey: 'VALUE_TYPES.STRING', descriptionKey: 'VALUE_TYPES.STRING_HINT' },
  { value: 'dimensions', labelKey: 'VALUE_TYPES.DIMENSIONS', descriptionKey: 'VALUE_TYPES.DIMENSIONS_HINT' },
  { value: 'number', labelKey: 'VALUE_TYPES.NUMBER', descriptionKey: 'VALUE_TYPES.NUMBER_HINT' },
];

const VISUALIZATION_OPTIONS: { field: keyof VisualizationConfig; key: string }[] = [
  { field: 'requiresSeamlessTexture', key: 'SEAMLESS' },
  { field: 'requires3DModel', key: 'MODEL_3D' },
  { field: 'supportsPlankImages', key: 'PLANKS' },
  { field: 'supportsPatterns', key: 'PATTERNS' },
  { field: 'showApplicationTypes', key: 'APPLICATION_TYPES' },
  { field: 'showProductType', key: 'PRODUCT_TYPE' },
];

const K = 'ADMIN_CATALOG.FAMILY_EDITOR';

// =============================================================================
// Small building blocks
// =============================================================================

/** Yes / no in a read-only table cell: a tick, or a quiet dash (and the word, for screen readers). */
const YesNo = ({ on, yes, no }: { on: boolean; yes: string; no: string }) => (
  <span className="inline-flex justify-center">
    {on ? (
      <Check className="size-4" aria-hidden />
    ) : (
      <span className="text-muted-foreground" aria-hidden>
        —
      </span>
    )}
    <span className="sr-only">{on ? yes : no}</span>
  </span>
);

/** 2D or 3D, as a segmented pair (the grammar of the kit's pill tabs). */
const DimensionPicker = ({
  value,
  onChange,
  labels,
  size = 'sm',
  ariaLabel,
}: {
  value: 2 | 3 | undefined;
  onChange: (count: 2 | 3) => void;
  labels: [string, string];
  size?: 'sm' | 'md';
  ariaLabel?: string;
}) => (
  <div role="group" aria-label={ariaLabel} className="inline-flex gap-0.5 rounded-lg bg-muted p-0.5">
    {([2, 3] as const).map((count, i) => {
      const selected = count === 2 ? (value || 2) === 2 : value === 3;
      return (
        <button
          key={count}
          type="button"
          aria-pressed={selected}
          onClick={() => onChange(count)}
          className={cn(
            'rounded-md whitespace-nowrap transition-colors',
            size === 'sm' ? 'px-2 py-0.5 text-[11px]' : 'px-3 py-1 text-xs',
            selected
              ? 'bg-background font-medium text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {labels[i]}
        </button>
      );
    })}
  </div>
);

/** A switch and its label on one line (the small toggles of the add forms). */
const SwitchRow = ({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
}) => (
  <Label className="flex items-center gap-2 font-normal">
    <Switch checked={checked} onCheckedChange={onChange} />
    <span className="text-sm">{label}</span>
  </Label>
);

const EMPTY_ATTRIBUTE: Partial<FamilyAttribute> = {
  fieldName: '',
  fieldLabel: '',
  fieldType: 'text',
  category: 'family_specific',
  required: false,
  visibleInList: false,
  visibleInFilters: false,
  options: [],
};

// =============================================================================
// Main Component
// =============================================================================

export const EditProductFamily = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { rt } = useResolveTranslation();
  const { familyId } = useParams<{ familyId: string }>();
  const productFamiliesApi = useProductFamiliesApi();

  const isNew = !familyId;

  // Form state
  const [formData, setFormData] = useState<Partial<ProductFamily>>({
    name: '',
    slug: '',
    description: '',
    isActive: true,
    isDefault: false,
    attributes: [...CORE_ATTRIBUTES],
    variantAxes: [
      { name: 'color', label: 'Color', enabled: true, required: true, valueType: 'string', allowCustomValues: true },
      { name: 'size', label: 'Size', enabled: true, required: false, valueType: 'dimensions', allowCustomValues: true },
    ],
    visualizationConfig: {
      requiresSeamlessTexture: false,
      requires3DModel: false,
      supportsPlankImages: false,
      supportsPatterns: false,
      patternOptions: [],
      defaultApplicationTypes: [],
    },
  });
  const [originalFormData, setOriginalFormData] = useState<Partial<ProductFamily> | null>(null);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // New attribute form
  const [newAttribute, setNewAttribute] = useState<Partial<FamilyAttribute>>({ ...EMPTY_ATTRIBUTE });
  const [showNewAttributeForm, setShowNewAttributeForm] = useState(false);

  // New variant axis form
  const [newVariantAxis, setNewVariantAxis] = useState<Partial<VariantAxisConfig>>({
    name: '',
    label: '',
    enabled: true,
    required: false,
    valueType: 'string',
    allowCustomValues: true,
    predefinedValues: [],
    isGroupingAxis: false,
  });
  const [showNewAxisForm, setShowNewAxisForm] = useState(false);
  const [newAxisPredefinedValueInput, setNewAxisPredefinedValueInput] = useState('');

  // Inline editing
  const [editingAttributeIndex, setEditingAttributeIndex] = useState<number | null>(null);
  const [editingAxisIndex, setEditingAxisIndex] = useState<number | null>(null);

  // Track changes
  const hasChanges = JSON.stringify(formData) !== JSON.stringify(originalFormData);

  // =============================================================================
  // Effects
  // =============================================================================

  useEffect(() => {
    if (!isNew && familyId) {
      const fetchFamily = async () => {
        setLoading(true);
        try {
          const family = await productFamiliesApi.findOneFamily(familyId);
          if (family) {
            setFormData(family);
            setOriginalFormData(family);
            dispatch(setActiveFamily(family));
          }
        } catch (error) {
          console.error('Error fetching family:', error);
        } finally {
          setLoading(false);
        }
      };
      fetchFamily();
    } else {
      setOriginalFormData(formData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [familyId, isNew]);

  // =============================================================================
  // Handlers
  // =============================================================================

  const handleInputChange = (field: keyof ProductFamily, value: any) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      if (field === 'name' && isNew) {
        const nameStr = typeof value === 'string' ? value : resolveTranslation(value, 'en');
        updated.slug = generateSlug(nameStr);
      }
      return updated;
    });
  };

  const handleVisualizationChange = (field: keyof VisualizationConfig, value: any) => {
    setFormData(prev => ({
      ...prev,
      visualizationConfig: {
        ...prev.visualizationConfig,
        [field]: value,
      },
    }));
  };

  const handleAddAttribute = () => {
    if (!newAttribute.fieldName || !resolveTranslation(newAttribute.fieldLabel, 'en')) return;

    const attribute: FamilyAttribute = {
      fieldName: newAttribute.fieldName,
      fieldLabel: newAttribute.fieldLabel,
      fieldType: newAttribute.fieldType as AttributeFieldType,
      category: 'family_specific',
      required: newAttribute.required || false,
      visibleInList: newAttribute.visibleInList || false,
      visibleInFilters: newAttribute.visibleInFilters || false,
      order: (formData.attributes?.length || 0) + 1,
      options: newAttribute.options,
    };

    setFormData(prev => ({
      ...prev,
      attributes: [...(prev.attributes || []), attribute],
    }));

    setNewAttribute({ ...EMPTY_ATTRIBUTE });
    setShowNewAttributeForm(false);
  };

  const handleRemoveAttribute = (index: number) => {
    const attr = formData.attributes?.[index];
    if (attr?.category === 'core') return;

    setFormData(prev => ({
      ...prev,
      attributes: prev.attributes?.filter((_, i) => i !== index),
    }));
  };

  const handleUpdateAttribute = (index: number, field: keyof FamilyAttribute, value: any) => {
    setFormData(prev => ({
      ...prev,
      attributes: prev.attributes?.map((attr, i) =>
        i === index ? { ...attr, [field]: value } : attr
      ),
    }));
  };

  const handleToggleVariantAxis = (index: number, field: keyof VariantAxisConfig, value: any) => {
    setFormData(prev => ({
      ...prev,
      variantAxes: prev.variantAxes?.map((axis, i) =>
        i === index ? { ...axis, [field]: value } : axis
      ),
    }));
  };

  // Variant Axis handlers
  const handleAddVariantAxis = () => {
    if (!newVariantAxis.name || !resolveTranslation(newVariantAxis.label, 'en')) return;

    const axis: VariantAxisConfig = {
      name: newVariantAxis.name,
      label: newVariantAxis.label,
      enabled: newVariantAxis.enabled ?? true,
      required: newVariantAxis.required ?? false,
      valueType: newVariantAxis.valueType as VariantAxisValueType || 'string',
      allowCustomValues: newVariantAxis.allowCustomValues ?? true,
      predefinedValues: newVariantAxis.predefinedValues,
      // Include dimensionCount only for dimensions type
      ...(newVariantAxis.valueType === 'dimensions' && { dimensionCount: newVariantAxis.dimensionCount || 2 }),
      // Include isGroupingAxis if set
      ...(newVariantAxis.isGroupingAxis && { isGroupingAxis: true }),
    };

    setFormData(prev => ({
      ...prev,
      variantAxes: [...(prev.variantAxes || []), axis],
    }));

    setNewVariantAxis({
      name: '',
      label: '',
      enabled: true,
      required: false,
      valueType: 'string',
      allowCustomValues: true,
      predefinedValues: [],
      dimensionCount: undefined,
      isGroupingAxis: false,
    });
    setShowNewAxisForm(false);
  };

  const handleRemoveVariantAxis = (index: number) => {
    setFormData(prev => ({
      ...prev,
      variantAxes: prev.variantAxes?.filter((_, i) => i !== index),
    }));
  };

  const handleMoveVariantAxisUp = (index: number) => {
    if (index === 0) return;
    setFormData(prev => {
      const axes = [...(prev.variantAxes || [])];
      [axes[index - 1], axes[index]] = [axes[index], axes[index - 1]];
      return { ...prev, variantAxes: axes };
    });
  };

  const handleMoveVariantAxisDown = (index: number) => {
    setFormData(prev => {
      const axes = [...(prev.variantAxes || [])];
      if (index >= axes.length - 1) return prev;
      [axes[index], axes[index + 1]] = [axes[index + 1], axes[index]];
      return { ...prev, variantAxes: axes };
    });
  };

  const handleAddAxisPredefinedValue = () => {
    if (!newAxisPredefinedValueInput.trim()) return;
    setNewVariantAxis(prev => ({
      ...prev,
      predefinedValues: [...(prev.predefinedValues || []), newAxisPredefinedValueInput.trim()],
    }));
    setNewAxisPredefinedValueInput('');
  };

  const handleRemoveAxisPredefinedValue = (valueToRemove: string) => {
    setNewVariantAxis(prev => ({
      ...prev,
      predefinedValues: prev.predefinedValues?.filter(v => v !== valueToRemove),
    }));
  };

  const handleSave = async () => {
    if (!resolveTranslation(formData.name, 'en') || !formData.slug) return;

    setSaving(true);
    try {
      if (isNew) {
        const payload: CreateProductFamilyPayload = {
          name: formData.name,
          slug: formData.slug,
          description: formData.description,
          icon: formData.icon,
          isActive: formData.isActive,
          attributes: formData.attributes,
          variantAxes: formData.variantAxes,
          visualizationConfig: formData.visualizationConfig,
        };
        const created = await productFamiliesApi.createFamily(payload);
        dispatch(addProductFamily(created));
        toast.success(t(`${K}.CREATED`));
      } else if (familyId) {
        const updated = await productFamiliesApi.updateFamily(familyId, formData);
        dispatch(updateProductFamily(updated));
        toast.success(t(`${K}.SAVED`));
      }
      navigate('/product-families');
    } catch (error) {
      console.error('Error saving family:', error);
      toast.error(t('ADMIN_CATALOG.EDITOR.SAVE_FAILED'));
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    if (originalFormData) {
      setFormData(originalFormData);
    }
  };

  const cancelNewAttribute = () => {
    setShowNewAttributeForm(false);
    setNewAttribute({ ...EMPTY_ATTRIBUTE });
  };

  const cancelNewAxis = () => {
    setShowNewAxisForm(false);
    setNewVariantAxis({
      name: '',
      label: '',
      enabled: true,
      required: false,
      valueType: 'string',
      allowCustomValues: true,
      predefinedValues: [],
      isGroupingAxis: false,
    });
    setNewAxisPredefinedValueInput('');
  };

  // =============================================================================
  // Render
  // =============================================================================

  const parents = [{ label: t('ADMIN_SHELL.NAV.FAMILIES'), href: '/product-families' }];
  const backToList = () => navigate('/product-families');

  if (loading) {
    return (
      <AdminPage
        title={t('ADMIN_SHELL.COMMON.LOADING')}
        parents={parents}
        className="mx-auto w-full max-w-6xl"
        header={<Skeleton className="h-8 w-64" />}
      >
        <div className="flex min-h-[50vh] flex-col items-center justify-center gap-3" data-testid="family-editor-loading">
          <Loader2 className="size-6 animate-spin text-muted-foreground" />
          <p className="text-sm text-muted-foreground">{t(`${K}.LOADING`)}</p>
        </div>
      </AdminPage>
    );
  }

  const title = isNew ? t(`${K}.NEW_TITLE`) : rt(formData.name) || t(`${K}.EDIT_TITLE`);
  const attributes = formData.attributes || [];
  const variantAxes = formData.variantAxes || [];
  const yes = t('ADMIN_CATALOG.COMMON.YES');
  const no = t('ADMIN_CATALOG.COMMON.NO');
  const fieldTypeLabel = (type: AttributeFieldType) => {
    const found = FIELD_TYPES.find(ft => ft.value === type);
    return found ? t(`${K}.${found.labelKey}`) : type;
  };
  const valueTypeLabel = (type: VariantAxisValueType) => {
    const found = AXIS_VALUE_TYPES.find(vt => vt.value === type);
    return found ? t(`${K}.${found.labelKey}`) : type;
  };

  return (
    <AdminPage
      title={title}
      parents={parents}
      className="mx-auto w-full max-w-6xl"
      header={
        <EditorHeader title={title} description={t(`${K}.DESCRIPTION`)} onBack={backToList} backLabel={t(`${K}.BACK`)} />
      }
    >
      {/* General Information */}
      <EditorSection title={t(`${K}.GENERAL.TITLE`)} testId="family-general">
        <div className="flex flex-col gap-5">
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            {/* Family Name */}
            <TranslatableInput
              value={formData.name || ''}
              onChange={val => handleInputChange('name', val)}
              label={t(`${K}.GENERAL.NAME`)}
              required
              placeholder={t(`${K}.GENERAL.NAME_PLACEHOLDER`)}
            />

            {/* Slug */}
            <div>
              <Label htmlFor="family-slug" className="mb-1 block text-xs text-muted-foreground">
                {t(`${K}.GENERAL.SLUG`)} <span className="text-destructive">*</span>
              </Label>
              <LangRowSpacer />
              <Input
                id="family-slug"
                className="font-mono"
                value={formData.slug || ''}
                onChange={e => handleInputChange('slug', e.target.value)}
                placeholder="surface-products"
                disabled={!isNew && formData.isSystemFamily}
                aria-describedby="family-slug-hint"
              />
              <p id="family-slug-hint" className="mt-1 text-xs text-muted-foreground">
                {t(`${K}.GENERAL.SLUG_HINT`)}
              </p>
            </div>
          </div>

          {/* Description */}
          <TranslatableInput
            value={formData.description || ''}
            onChange={val => handleInputChange('description', val)}
            label={t(`${K}.GENERAL.DESCRIPTION`)}
            multiline
            rows={3}
            placeholder={t(`${K}.GENERAL.DESCRIPTION_PLACEHOLDER`)}
          />

          {/* Toggles */}
          <div className="grid grid-cols-1 gap-x-8 border-t md:grid-cols-2">
            <ToggleField
              id="family-active"
              label={t(`${K}.GENERAL.ACTIVE`)}
              description={t(`${K}.GENERAL.ACTIVE_HINT`)}
              checked={formData.isActive || false}
              onCheckedChange={checked => handleInputChange('isActive', checked)}
            />
            <ToggleField
              id="family-default"
              label={t(`${K}.GENERAL.DEFAULT`)}
              description={t(`${K}.GENERAL.DEFAULT_HINT`)}
              checked={formData.isDefault || false}
              onCheckedChange={checked => handleInputChange('isDefault', checked)}
            />
          </div>
        </div>
      </EditorSection>

      {/* Attributes */}
      <EditorSection
        title={t(`${K}.ATTRIBUTES.TITLE`)}
        description={t(`${K}.ATTRIBUTES.DESCRIPTION`)}
        badge={t(`${K}.ATTRIBUTES.COUNT`, { count: attributes.length })}
        collapsible
        defaultExpanded
        flush
        testId="family-attributes"
      >
        <div className="border-t">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="ps-4 text-start">{t(`${K}.ATTRIBUTES.COL_ATTRIBUTE`)}</TableHead>
                <TableHead className="text-start">{t(`${K}.ATTRIBUTES.COL_TYPE`)}</TableHead>
                <TableHead className="text-start">{t(`${K}.ATTRIBUTES.COL_CATEGORY`)}</TableHead>
                <TableHead className="text-center">{t(`${K}.ATTRIBUTES.COL_REQUIRED`)}</TableHead>
                <TableHead className="text-center">{t(`${K}.ATTRIBUTES.COL_LIST`)}</TableHead>
                <TableHead className="text-center">{t(`${K}.ATTRIBUTES.COL_FILTER`)}</TableHead>
                <TableHead className="w-20 pe-4">
                  <span className="sr-only">{t('ADMIN_SHELL.COMMON.ACTIONS')}</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {attributes.map((attr, index) => {
                const editing = editingAttributeIndex === index;
                return [
                  <TableRow
                    key={attr.fieldName}
                    className={cn(editing && 'border-b-0 bg-muted/50 hover:bg-muted/50')}
                    data-testid="family-attribute-row"
                  >
                    <TableCell className="ps-4">
                      <p className="text-sm font-medium">{rt(attr.fieldLabel)}</p>
                      <p className="font-mono text-[11px] text-muted-foreground">{attr.fieldName}</p>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{fieldTypeLabel(attr.fieldType)}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {attr.category === 'core' ? t(`${K}.ATTRIBUTES.CORE`) : t(`${K}.ATTRIBUTES.CUSTOM`)}
                    </TableCell>
                    <TableCell className="text-center">
                      <YesNo on={!!attr.required} yes={yes} no={no} />
                    </TableCell>
                    <TableCell className="text-center">
                      <YesNo on={!!attr.visibleInList} yes={yes} no={no} />
                    </TableCell>
                    <TableCell className="text-center">
                      <YesNo on={!!attr.visibleInFilters} yes={yes} no={no} />
                    </TableCell>
                    <TableCell className="pe-4">
                      <div className="flex justify-end gap-0.5">
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          aria-label={editing ? t(`${K}.ATTRIBUTES.CLOSE`) : t(`${K}.ATTRIBUTES.EDIT`)}
                          aria-expanded={editing}
                          onClick={() => setEditingAttributeIndex(editing ? null : index)}
                        >
                          {editing ? <X /> : <Pencil />}
                        </Button>
                        {attr.category !== 'core' && (
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            aria-label={t(`${K}.ATTRIBUTES.REMOVE`)}
                            className="text-muted-foreground hover:bg-destructive/5 hover:text-destructive"
                            onClick={() => handleRemoveAttribute(index)}
                          >
                            <Trash2 />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>,
                  editing && (
                    <TableRow key={`${attr.fieldName}-edit`} className="bg-muted/50 hover:bg-muted/50">
                      <TableCell colSpan={7} className="px-4 pt-1 pb-4 whitespace-normal">
                        {/* Inline Edit Form */}
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                          <TranslatableInput
                            value={attr.fieldLabel}
                            onChange={val => handleUpdateAttribute(index, 'fieldLabel', val)}
                            label={t(`${K}.ATTRIBUTES.FIELD_LABEL`)}
                          />
                          <TranslatableInput
                            value={attr.placeholder || ''}
                            onChange={val => handleUpdateAttribute(index, 'placeholder', val)}
                            label={t(`${K}.ATTRIBUTES.PLACEHOLDER`)}
                          />
                          <div className="md:col-span-2">
                            <TranslatableInput
                              value={attr.helpText || ''}
                              onChange={val => handleUpdateAttribute(index, 'helpText', val)}
                              label={t(`${K}.ATTRIBUTES.HELP_TEXT`)}
                            />
                          </div>
                          {(attr.fieldType === 'select' || attr.fieldType === 'multioption') && (
                            <div className="md:col-span-2">
                              <TranslatableChipInput
                                value={attr.options || []}
                                onChange={val => handleUpdateAttribute(index, 'options', val)}
                                label={t(`${K}.ATTRIBUTES.OPTIONS`)}
                              />
                            </div>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ),
                ];
              })}
            </TableBody>
          </Table>
        </div>

        {/* Add New Attribute */}
        <div className="border-t p-4">
          {showNewAttributeForm ? (
            <div className="rounded-lg border p-4" data-testid="family-new-attribute">
              <p className="mb-4 text-sm font-medium">{t(`${K}.ATTRIBUTES.NEW`)}</p>
              <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-3">
                <TranslatableInput
                  value={newAttribute.fieldLabel || ''}
                  onChange={val => {
                    const nameStr = resolveTranslation(val, 'en');
                    setNewAttribute(prev => ({
                      ...prev,
                      fieldLabel: val,
                      fieldName: nameStr.toLowerCase().replace(/[^a-z0-9]+/g, '_'),
                    }));
                  }}
                  label={t(`${K}.ATTRIBUTES.FIELD_LABEL`)}
                  required
                  placeholder={t(`${K}.ATTRIBUTES.FIELD_LABEL_PLACEHOLDER`)}
                />
                <div>
                  <Label htmlFor="attribute-field-name" className="mb-1 block text-xs text-muted-foreground">
                    {t(`${K}.ATTRIBUTES.FIELD_NAME`)} <span className="text-destructive">*</span>
                  </Label>
                  <LangRowSpacer />
                  <Input
                    id="attribute-field-name"
                    className="font-mono"
                    value={newAttribute.fieldName || ''}
                    onChange={e => setNewAttribute(prev => ({ ...prev, fieldName: e.target.value }))}
                    placeholder={t(`${K}.ATTRIBUTES.FIELD_NAME_PLACEHOLDER`)}
                  />
                </div>
                <div>
                  <Label htmlFor="attribute-field-type" className="mb-1 block text-xs text-muted-foreground">
                    {t(`${K}.ATTRIBUTES.FIELD_TYPE`)}
                  </Label>
                  <LangRowSpacer />
                  <Select
                    value={newAttribute.fieldType || 'text'}
                    onValueChange={value =>
                      setNewAttribute(prev => ({ ...prev, fieldType: value as AttributeFieldType }))
                    }
                  >
                    <SelectTrigger id="attribute-field-type" className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FIELD_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          {t(`${K}.${type.labelKey}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Options for select/multioption */}
              {(newAttribute.fieldType === 'select' || newAttribute.fieldType === 'multioption') && (
                <div className="mb-4">
                  <TranslatableChipInput
                    value={newAttribute.options || []}
                    onChange={opts => setNewAttribute(prev => ({ ...prev, options: opts }))}
                    label={t(`${K}.ATTRIBUTES.OPTIONS`)}
                    placeholder={t(`${K}.ATTRIBUTES.OPTIONS_PLACEHOLDER`)}
                  />
                </div>
              )}

              {/* Toggles */}
              <div className="mb-5 flex flex-wrap gap-6">
                <SwitchRow
                  checked={newAttribute.required || false}
                  onChange={checked => setNewAttribute(prev => ({ ...prev, required: checked }))}
                  label={t(`${K}.ATTRIBUTES.REQUIRED`)}
                />
                <SwitchRow
                  checked={newAttribute.visibleInList || false}
                  onChange={checked => setNewAttribute(prev => ({ ...prev, visibleInList: checked }))}
                  label={t(`${K}.ATTRIBUTES.SHOW_IN_LIST`)}
                />
                <SwitchRow
                  checked={newAttribute.visibleInFilters || false}
                  onChange={checked => setNewAttribute(prev => ({ ...prev, visibleInFilters: checked }))}
                  label={t(`${K}.ATTRIBUTES.SHOW_IN_FILTERS`)}
                />
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={cancelNewAttribute}>
                  {t('ADMIN_SHELL.COMMON.CANCEL')}
                </Button>
                <Button
                  onClick={handleAddAttribute}
                  disabled={!newAttribute.fieldName || !resolveTranslation(newAttribute.fieldLabel, 'en')}
                >
                  {t(`${K}.ATTRIBUTES.ADD`)}
                </Button>
              </div>
            </div>
          ) : (
            <Button variant="outline" size="sm" onClick={() => setShowNewAttributeForm(true)}>
              <Plus />
              {t(`${K}.ATTRIBUTES.ADD`)}
            </Button>
          )}
        </div>
      </EditorSection>

      {/* Variant Axes */}
      <EditorSection
        title={t(`${K}.AXES.TITLE`)}
        description={t(`${K}.AXES.DESCRIPTION`)}
        badge={t(`${K}.AXES.COUNT`, { count: variantAxes.length })}
        collapsible
        defaultExpanded
        flush
        testId="family-axes"
      >
        {variantAxes.length > 0 && (
          <div className="border-t">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="w-14 ps-4 text-start">{t(`${K}.AXES.COL_ON`)}</TableHead>
                  <TableHead className="w-10">
                    <span className="sr-only">{t(`${K}.AXES.COL_ORDER`)}</span>
                  </TableHead>
                  <TableHead className="text-start">{t(`${K}.AXES.COL_AXIS`)}</TableHead>
                  <TableHead className="text-start">{t(`${K}.AXES.COL_TYPE`)}</TableHead>
                  <TableHead className="text-center">{t(`${K}.AXES.COL_REQUIRED`)}</TableHead>
                  <TableHead className="text-center">{t(`${K}.AXES.COL_GROUP`)}</TableHead>
                  <TableHead className="text-center">{t(`${K}.AXES.COL_DIMS`)}</TableHead>
                  <TableHead className="w-20 pe-4">
                    <span className="sr-only">{t('ADMIN_SHELL.COMMON.ACTIONS')}</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {variantAxes.map((axis, index) => {
                  const editing = editingAxisIndex === index;
                  const axisLabel = rt(axis.label);
                  return [
                    <TableRow
                      key={axis.name}
                      className={cn(editing && 'border-b-0 bg-muted/50 hover:bg-muted/50')}
                      data-testid="family-axis-row"
                    >
                      {/* On */}
                      <TableCell className="ps-4">
                        <Switch
                          checked={axis.enabled}
                          onCheckedChange={checked => handleToggleVariantAxis(index, 'enabled', checked)}
                          aria-label={t(`${K}.AXES.ENABLE_AXIS`, { name: axisLabel })}
                        />
                      </TableCell>
                      {/* Reorder */}
                      <TableCell>
                        <div className="flex flex-col">
                          <button
                            type="button"
                            aria-label={t(`${K}.AXES.MOVE_UP`)}
                            onClick={() => handleMoveVariantAxisUp(index)}
                            disabled={index === 0}
                            className="rounded-sm text-muted-foreground transition-colors hover:text-foreground disabled:pointer-events-none disabled:opacity-30"
                          >
                            <ChevronUp className="size-4" />
                          </button>
                          <button
                            type="button"
                            aria-label={t(`${K}.AXES.MOVE_DOWN`)}
                            onClick={() => handleMoveVariantAxisDown(index)}
                            disabled={index === variantAxes.length - 1}
                            className="rounded-sm text-muted-foreground transition-colors hover:text-foreground disabled:pointer-events-none disabled:opacity-30"
                          >
                            <ChevronDown className="size-4" />
                          </button>
                        </div>
                      </TableCell>
                      {/* Axis */}
                      <TableCell>
                        <p className={cn('text-sm font-medium', !axis.enabled && 'text-muted-foreground')}>
                          {axisLabel}
                        </p>
                        <p className="text-[11px] text-muted-foreground">
                          <span className="font-mono">{axis.name}</span>
                          {axis.predefinedValues && axis.predefinedValues.length > 0
                            ? ` · ${t(`${K}.AXES.OPTIONS_COUNT`, { count: axis.predefinedValues.length })}`
                            : ''}
                        </p>
                      </TableCell>
                      {/* Type */}
                      <TableCell className="text-muted-foreground">{valueTypeLabel(axis.valueType)}</TableCell>
                      {/* Required */}
                      <TableCell className="text-center">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="inline-flex">
                              <Checkbox
                                checked={!!axis.required}
                                disabled={!axis.enabled}
                                onCheckedChange={() =>
                                  axis.enabled && handleToggleVariantAxis(index, 'required', !axis.required)
                                }
                                aria-label={t(`${K}.AXES.REQUIRED_FOR`, { name: axisLabel })}
                              />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent side="top">
                            {axis.required ? t(`${K}.AXES.REQUIRED`) : t(`${K}.AXES.OPTIONAL`)}
                          </TooltipContent>
                        </Tooltip>
                      </TableCell>
                      {/* Group */}
                      <TableCell className="text-center">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="inline-flex items-center gap-1">
                              <Checkbox
                                checked={!!axis.isGroupingAxis}
                                disabled={!axis.enabled}
                                onCheckedChange={() =>
                                  axis.enabled &&
                                  handleToggleVariantAxis(index, 'isGroupingAxis', !axis.isGroupingAxis)
                                }
                                aria-label={t(`${K}.AXES.GROUPING_FOR`, { name: axisLabel })}
                              />
                              {axis.isGroupingAxis && (
                                <Link2 className={cn('size-3 text-muted-foreground', !axis.enabled && 'opacity-40')} aria-hidden />
                              )}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent side="top">
                            {axis.isGroupingAxis ? t(`${K}.AXES.GROUPING_ON`) : t(`${K}.AXES.GROUPING_OFF`)}
                          </TooltipContent>
                        </Tooltip>
                      </TableCell>
                      {/* Dims */}
                      <TableCell className="text-center">
                        {axis.valueType === 'dimensions' && axis.enabled ? (
                          <DimensionPicker
                            value={axis.dimensionCount}
                            onChange={count => handleToggleVariantAxis(index, 'dimensionCount', count)}
                            labels={[t(`${K}.AXES.DIM_2D`), t(`${K}.AXES.DIM_3D`)]}
                            ariaLabel={t(`${K}.AXES.DIMENSION_COUNT`)}
                          />
                        ) : (
                          <span className="text-muted-foreground" aria-hidden>
                            —
                          </span>
                        )}
                      </TableCell>
                      {/* Edit / Delete */}
                      <TableCell className="pe-4">
                        <div className="flex justify-end gap-0.5">
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            aria-label={editing ? t(`${K}.AXES.CLOSE`) : t(`${K}.AXES.EDIT`)}
                            aria-expanded={editing}
                            onClick={() => setEditingAxisIndex(editing ? null : index)}
                          >
                            {editing ? <X /> : <Pencil />}
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            aria-label={t(`${K}.AXES.REMOVE`)}
                            className="text-muted-foreground hover:bg-destructive/5 hover:text-destructive"
                            onClick={() => handleRemoveVariantAxis(index)}
                          >
                            <Trash2 />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>,
                    editing && (
                      <TableRow key={`${axis.name}-edit`} className="bg-muted/50 hover:bg-muted/50">
                        <TableCell colSpan={8} className="px-4 pt-1 pb-4 whitespace-normal">
                          {/* Inline Edit Form */}
                          <div className="max-w-md">
                            <TranslatableInput
                              value={axis.label}
                              onChange={val => handleToggleVariantAxis(index, 'label', val)}
                              label={t(`${K}.AXES.LABEL`)}
                            />
                          </div>
                        </TableCell>
                      </TableRow>
                    ),
                  ];
                })}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Add New Variant Axis */}
        <div className="border-t p-4">
          {showNewAxisForm ? (
            <div className="rounded-lg border p-4" data-testid="family-new-axis">
              <p className="mb-4 text-sm font-medium">{t(`${K}.AXES.NEW`)}</p>
              <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-3">
                <TranslatableInput
                  value={newVariantAxis.label || ''}
                  onChange={val => {
                    const nameStr = resolveTranslation(val, 'en');
                    setNewVariantAxis(prev => ({
                      ...prev,
                      label: val,
                      name: nameStr.toLowerCase().replace(/[^a-z0-9]+/g, '_'),
                    }));
                  }}
                  label={t(`${K}.AXES.LABEL`)}
                  required
                  placeholder={t(`${K}.AXES.LABEL_PLACEHOLDER`)}
                />
                <div>
                  <Label htmlFor="axis-name" className="mb-1 block text-xs text-muted-foreground">
                    {t(`${K}.AXES.NAME`)} <span className="text-destructive">*</span>
                  </Label>
                  <LangRowSpacer />
                  <Input
                    id="axis-name"
                    className="font-mono"
                    value={newVariantAxis.name || ''}
                    onChange={e => setNewVariantAxis(prev => ({ ...prev, name: e.target.value }))}
                    placeholder={t(`${K}.AXES.NAME_PLACEHOLDER`)}
                    aria-describedby="axis-name-hint"
                  />
                  <p id="axis-name-hint" className="mt-1 text-xs text-muted-foreground">
                    {t(`${K}.AXES.NAME_HINT`)}
                  </p>
                </div>
                <div>
                  <Label htmlFor="axis-value-type" className="mb-1 block text-xs text-muted-foreground">
                    {t(`${K}.AXES.VALUE_TYPE`)}
                  </Label>
                  <LangRowSpacer />
                  <Select
                    value={newVariantAxis.valueType || 'string'}
                    onValueChange={value =>
                      setNewVariantAxis(prev => ({ ...prev, valueType: value as VariantAxisValueType }))
                    }
                  >
                    <SelectTrigger id="axis-value-type" className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {AXIS_VALUE_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          {t(`${K}.${type.labelKey}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t(
                      `${K}.${
                        (AXIS_VALUE_TYPES.find(vt => vt.value === (newVariantAxis.valueType || 'string')) ||
                          AXIS_VALUE_TYPES[0]).descriptionKey
                      }`,
                    )}
                  </p>
                </div>
              </div>

              {/* Dimension Count - only shown when valueType is 'dimensions' */}
              {newVariantAxis.valueType === 'dimensions' && (
                <div className="mb-4">
                  <p id="axis-dimension-count" className="mb-1 text-xs font-medium text-muted-foreground">
                    {t(`${K}.AXES.DIMENSION_COUNT`)}
                  </p>
                  <p className="mb-2 text-xs text-muted-foreground">{t(`${K}.AXES.DIMENSION_COUNT_HINT`)}</p>
                  <DimensionPicker
                    value={newVariantAxis.dimensionCount}
                    onChange={count => setNewVariantAxis(prev => ({ ...prev, dimensionCount: count }))}
                    labels={[t(`${K}.AXES.DIM_2D_LONG`), t(`${K}.AXES.DIM_3D_LONG`)]}
                    size="md"
                    ariaLabel={t(`${K}.AXES.DIMENSION_COUNT`)}
                  />
                </div>
              )}

              {/* Predefined Values */}
              <div className="mb-4">
                <Label htmlFor="axis-predefined-value" className="mb-1 block text-xs text-muted-foreground">
                  {t(`${K}.AXES.PREDEFINED`)}
                </Label>
                <p className="mb-2 text-xs text-muted-foreground">{t(`${K}.AXES.PREDEFINED_HINT`)}</p>
                {!!newVariantAxis.predefinedValues?.length && (
                  <div className="mb-2 flex flex-wrap gap-1.5">
                    {newVariantAxis.predefinedValues.map(value => (
                      <Badge key={value} variant="outline" className="gap-1 pe-1 font-normal">
                        <span>{value}</span>
                        <button
                          type="button"
                          aria-label={t(`${K}.AXES.REMOVE_VALUE`, { value })}
                          className="grid size-4 place-items-center rounded-sm text-muted-foreground transition-colors hover:text-destructive"
                          onClick={() => handleRemoveAxisPredefinedValue(value)}
                        >
                          <X className="size-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
                <div className="flex max-w-md gap-2">
                  <Input
                    id="axis-predefined-value"
                    className="flex-1"
                    value={newAxisPredefinedValueInput}
                    onChange={e => setNewAxisPredefinedValueInput(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleAddAxisPredefinedValue()}
                    placeholder={t(`${K}.AXES.PREDEFINED_PLACEHOLDER`)}
                  />
                  <Button variant="outline" onClick={handleAddAxisPredefinedValue}>
                    <Plus />
                    {t(`${K}.AXES.ADD_VALUE`)}
                  </Button>
                </div>
              </div>

              {/* Toggles */}
              <div className="mb-5 flex flex-wrap gap-6">
                <SwitchRow
                  checked={newVariantAxis.enabled ?? true}
                  onChange={checked => setNewVariantAxis(prev => ({ ...prev, enabled: checked }))}
                  label={t(`${K}.AXES.ENABLED`)}
                />
                <SwitchRow
                  checked={newVariantAxis.required || false}
                  onChange={checked => setNewVariantAxis(prev => ({ ...prev, required: checked }))}
                  label={t(`${K}.AXES.REQUIRED`)}
                />
                <SwitchRow
                  checked={newVariantAxis.allowCustomValues ?? true}
                  onChange={checked => setNewVariantAxis(prev => ({ ...prev, allowCustomValues: checked }))}
                  label={t(`${K}.AXES.ALLOW_CUSTOM`)}
                />
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Label className="flex items-center gap-2 font-normal">
                      <Switch
                        checked={newVariantAxis.isGroupingAxis || false}
                        onCheckedChange={checked =>
                          setNewVariantAxis(prev => ({ ...prev, isGroupingAxis: checked }))
                        }
                      />
                      <Link2
                        aria-hidden
                        className={cn(
                          'size-3.5',
                          newVariantAxis.isGroupingAxis ? 'text-foreground' : 'text-muted-foreground',
                        )}
                      />
                      <span className="text-sm">{t(`${K}.AXES.GROUPING`)}</span>
                    </Label>
                  </TooltipTrigger>
                  <TooltipContent side="top">{t(`${K}.AXES.GROUPING_HINT`)}</TooltipContent>
                </Tooltip>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={cancelNewAxis}>
                  {t('ADMIN_SHELL.COMMON.CANCEL')}
                </Button>
                <Button
                  onClick={handleAddVariantAxis}
                  disabled={!newVariantAxis.name || !resolveTranslation(newVariantAxis.label, 'en')}
                >
                  {t(`${K}.AXES.ADD`)}
                </Button>
              </div>
            </div>
          ) : (
            <Button variant="outline" size="sm" onClick={() => setShowNewAxisForm(true)}>
              <Plus />
              {t(`${K}.AXES.ADD`)}
            </Button>
          )}
        </div>
      </EditorSection>

      {/* Visualization Configuration */}
      <EditorSection
        title={t(`${K}.VISUALIZATION.TITLE`)}
        description={t(`${K}.VISUALIZATION.DESCRIPTION`)}
        collapsible
        defaultExpanded
        testId="family-visualization"
      >
        <div className="grid grid-cols-1 gap-x-8 md:grid-cols-2">
          {VISUALIZATION_OPTIONS.map(opt => (
            <ToggleField
              key={opt.field}
              id={`family-viz-${opt.field}`}
              className="border-t"
              label={t(`${K}.VISUALIZATION.${opt.key}`)}
              description={t(`${K}.VISUALIZATION.${opt.key}_HINT`)}
              checked={Boolean(formData.visualizationConfig?.[opt.field])}
              onCheckedChange={checked => handleVisualizationChange(opt.field, checked)}
            />
          ))}
        </div>
      </EditorSection>

      <SaveBar
        dirty={hasChanges}
        saving={saving}
        disabled={!resolveTranslation(formData.name, 'en') || !formData.slug}
        onSave={handleSave}
        onDiscard={handleDiscard}
        saveLabel={isNew ? t(`${K}.CREATE`) : t('ADMIN_CATALOG.EDITOR.SAVE_CHANGES')}
        testId="editor-save"
      />
    </AdminPage>
  );
};

export default EditProductFamily;
